import { useCallback } from 'react';

/**
 * Centralized analytics hook for tracking events throughout the application
 *
 * When analytics service is configured, update the implementation inside this hook
 * and all tracking throughout the app will automatically work.
 *
 * @example
 * const { trackEvent, trackPageView, trackCTA } = useAnalytics();
 *
 * // Track a generic event
 * trackEvent('video_play', { video_id: '123', duration: 30 });
 *
 * // Track a CTA click
 * trackCTA('header_cta', 'Get Started');
 *
 * // Track a page view
 * trackPageView('/about');
 *
 * @returns {Object} Analytics tracking methods
 */
export function useAnalytics() {
  /**
   * Track a generic event
   * @param {string} eventName - Name of the event
   * @param {Object} [eventData] - Additional event data
   */
  const trackEvent = useCallback((eventName, eventData = {}) => {
    // TODO: Implement actual analytics tracking when service is configured
    // Example implementations:

    // Google Analytics 4
    // if (typeof gtag !== 'undefined') {
    //   gtag('event', eventName, eventData);
    // }

    // Segment
    // if (typeof analytics !== 'undefined') {
    //   analytics.track(eventName, eventData);
    // }

    // Mixpanel
    // if (typeof mixpanel !== 'undefined') {
    //   mixpanel.track(eventName, eventData);
    // }

    // PostHog
    // if (typeof posthog !== 'undefined') {
    //   posthog.capture(eventName, eventData);
    // }

    // Development logging - safe check for process.env
    if (
      typeof process !== 'undefined' &&
      process.env?.NODE_ENV === 'development'
    ) {
      // eslint-disable-next-line no-console
      console.log('📊 Analytics Event:', eventName, eventData);
    }
  }, []);

  /**
   * Track a page view
   * @param {string} pagePath - Path of the page
   * @param {Object} [pageData] - Additional page data
   */
  const trackPageView = useCallback(
    (pagePath, pageData = {}) => {
      trackEvent('page_view', {
        page_path: pagePath,
        page_location: window.location.href,
        page_title: document.title,
        ...pageData,
      });
    },
    [trackEvent]
  );

  /**
   * Track a CTA (Call-to-Action) click
   * @param {string} ctaLocation - Where the CTA is located (e.g., 'header', 'marketplace', 'partner_marquee')
   * @param {string} ctaText - Text of the CTA button
   * @param {Object} [additionalData] - Any additional data to track
   */
  const trackCTA = useCallback(
    (ctaLocation, ctaText, additionalData = {}) => {
      trackEvent('cta_click', {
        cta_location: ctaLocation,
        cta_text: ctaText,
        timestamp: new Date().toISOString(),
        ...additionalData,
      });
    },
    [trackEvent]
  );

  /**
   * Track a form submission
   * @param {string} formName - Name of the form
   * @param {Object} [formData] - Form data (be careful not to include PII)
   */
  const trackFormSubmit = useCallback(
    (formName, formData = {}) => {
      trackEvent('form_submit', {
        form_name: formName,
        ...formData,
      });
    },
    [trackEvent]
  );

  /**
   * Track an error
   * @param {string} errorMessage - Error message
   * @param {Object} [errorData] - Additional error context
   */
  const trackError = useCallback(
    (errorMessage, errorData = {}) => {
      trackEvent('error', {
        error_message: errorMessage,
        ...errorData,
      });
    },
    [trackEvent]
  );

  /**
   * Track user engagement (scroll depth, time on page, etc.)
   * @param {string} engagementType - Type of engagement
   * @param {Object} [engagementData] - Engagement data
   */
  const trackEngagement = useCallback(
    (engagementType, engagementData = {}) => {
      trackEvent('user_engagement', {
        engagement_type: engagementType,
        ...engagementData,
      });
    },
    [trackEvent]
  );

  /**
   * Track a conversion
   * @param {string} conversionType - Type of conversion
   * @param {Object} [conversionData] - Conversion data
   */
  const trackConversion = useCallback(
    (conversionType, conversionData = {}) => {
      trackEvent('conversion', {
        conversion_type: conversionType,
        ...conversionData,
      });
    },
    [trackEvent]
  );

  return {
    trackEvent,
    trackPageView,
    trackCTA,
    trackFormSubmit,
    trackError,
    trackEngagement,
    trackConversion,
  };
}

export default useAnalytics;
